import { z } from 'zod';

export const questionIdSchema = z.string().regex(/^Q\d{3}$/);
export const sourcePathSchema = z.string().min(1).refine((value) => !value.startsWith('/') && !value.includes('\\'), {
  message: 'Source paths must be relative POSIX paths'
});

export const documentKindSchema = z.enum([
  'root',
  'orientation',
  'week-overview',
  'project',
  'learn',
  'mini-project',
  'main-integration',
  'feedback'
]);

export const dayKindSchema = z.enum(['weekday', 'saturday', 'sunday']);

export const contentLinkSchema = z.object({
  label: z.string(),
  href: z.string().min(1),
  kind: z.enum(['external', 'internal', 'anchor', 'email']),
  resolvedSourcePath: sourcePathSchema.nullable(),
  targetExists: z.boolean(),
  includedInBuild: z.boolean()
});

export const checklistItemSchema = z.object({
  id: z.string().min(1),
  text: z.string().min(1),
  checkedInSource: z.boolean(),
  blockId: z.string().min(1)
});

export const contentBlockSchema = z.object({
  id: z.string().min(1),
  heading: z.string().nullable(),
  level: z.union([z.literal(0), z.literal(2)]),
  markdown: z.string().min(1),
  checklistIds: z.array(z.string().min(1))
});

export const contentDocumentSchema = z.object({
  id: z.string().min(1),
  kind: documentKindSchema,
  title: z.string().min(1),
  intendedWeekday: z.string().nullable(),
  sourcePath: sourcePathSchema,
  markdown: z.string().min(1),
  wordCount: z.number().int().nonnegative(),
  blocks: z.array(contentBlockSchema).min(1),
  checklists: z.array(checklistItemSchema),
  links: z.array(contentLinkSchema),
  questionIds: z.array(questionIdSchema)
});

export const reflectionFieldSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  input: z.enum(['text', 'long-text', 'score']),
  blockId: z.string().min(1)
});

export const dailyMapRowSchema = z.object({
  dayNumber: z.number().int().min(1).max(7),
  weekday: z.string().min(1),
  learning: z.string().min(1),
  miniProject: z.string().min(1),
  mainProjectSlice: z.string().min(1)
});

export const daySchema = z.object({
  id: z.string().min(1),
  weekId: z.string().min(1),
  number: z.number().int().min(1).max(7),
  weekday: z.string().min(1),
  kind: dayKindSchema,
  title: z.string().min(1),
  miniProjectTitle: z.string().min(1),
  questionIds: z.array(questionIdSchema),
  completionChecklistIds: z.array(z.string().min(1)),
  reflectionFields: z.array(reflectionFieldSchema),
  requiresDailyRetrospective: z.boolean(),
  documents: z.object({
    learn: contentDocumentSchema,
    miniProject: contentDocumentSchema,
    mainIntegration: contentDocumentSchema,
    feedback: contentDocumentSchema
  })
});

export const projectScopeItemSchema = z.object({
  id: z.string().regex(/^[A-Z]{2,5}-\d+$/),
  text: z.string().min(1)
});

export const weekSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string().regex(/^week-\d{2}$/),
  slug: z.string().regex(/^week-\d{2}-[a-z0-9-]+$/),
  number: z.number().int().min(1),
  title: z.string().min(1),
  mainProject: z.string().min(1),
  milestone: z.string().min(1),
  outcome: z.string().min(1),
  weeklyQuestionIds: z.array(questionIdSchema).length(5),
  sourceDirectory: sourcePathSchema,
  overview: contentDocumentSchema,
  project: contentDocumentSchema,
  projectScope: z.array(projectScopeItemSchema),
  dailyMap: z.array(dailyMapRowSchema).length(7),
  days: z.array(daySchema).length(7)
});

export const questionOccurrenceSchema = z.object({
  documentId: z.string().min(1),
  sourcePath: sourcePathSchema,
  dayId: z.string().nullable(),
  role: z.enum(['question', 'answer', 'reference'])
});

export const interviewQuestionSchema = z.object({
  id: questionIdSchema,
  number: z.number().int().positive(),
  weekNumber: z.number().int().positive(),
  prompt: z.string().min(1),
  modelAnswerMarkdown: z.string().min(1),
  answerSourceDocumentId: z.string().min(1),
  occurrences: z.array(questionOccurrenceSchema).min(1)
});

export const interviewBankSchema = z.object({
  schemaVersion: z.literal(1),
  sourcePath: sourcePathSchema,
  selectedWeekNumbers: z.array(z.number().int().positive()).min(1),
  questions: z.array(interviewQuestionSchema).min(1)
});

export const orientationSchema = z.object({
  schemaVersion: z.literal(1),
  documents: z.array(contentDocumentSchema).min(1)
});

export const fixtureSchema = z.object({
  id: z.string().min(1),
  filename: z.string().min(1),
  sourcePath: sourcePathSchema,
  extension: z.string().min(1),
  language: z.string().min(1),
  content: z.string().min(1),
  referencedByDocumentIds: z.array(z.string().min(1)).min(1)
});

export const fixtureCollectionSchema = z.object({
  schemaVersion: z.literal(1),
  fixtures: z.array(fixtureSchema)
});

export const manifestWeekSchema = z.object({
  id: z.string().regex(/^week-\d{2}$/),
  number: z.number().int().positive(),
  slug: z.string().min(1),
  title: z.string().min(1),
  mainProject: z.string().min(1),
  milestone: z.string().min(1),
  file: z.string().regex(/^weeks\/week-\d{2}\.json$/),
  firstDayId: z.string().min(1),
  lastDayId: z.string().min(1)
});

export const manifestSchema = z.object({
  schemaVersion: z.literal(1),
  sourceDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  selectedWeekNumbers: z.array(z.number().int().positive()).min(1),
  completeCurriculum: z.boolean(),
  files: z.object({
    orientation: z.literal('orientation.json'),
    interviewBank: z.literal('interview-bank.json'),
    fixtures: z.literal('fixtures.json')
  }),
  totals: z.object({
    weeks: z.number().int().positive(),
    days: z.number().int().positive(),
    dailyDocuments: z.number().int().positive(),
    weekdayMiniProjects: z.number().int().nonnegative(),
    interviewQuestions: z.number().int().positive(),
    fixtures: z.number().int().nonnegative(),
    orientationDocuments: z.number().int().positive(),
    contentWords: z.number().int().positive()
  }),
  weeks: z.array(manifestWeekSchema).min(1),
  sourcePathToDocumentId: z.record(sourcePathSchema, z.string().min(1))
});

export const generatedBundleSchema = z.object({
  manifest: manifestSchema,
  orientation: orientationSchema,
  interviewBank: interviewBankSchema,
  fixtures: fixtureCollectionSchema,
  weeks: z.array(weekSchema).min(1)
});

export type ContentLink = z.infer<typeof contentLinkSchema>;
export type ChecklistItem = z.infer<typeof checklistItemSchema>;
export type ContentBlock = z.infer<typeof contentBlockSchema>;
export type ContentDocument = z.infer<typeof contentDocumentSchema>;
export type ReflectionField = z.infer<typeof reflectionFieldSchema>;
export type DailyMapRow = z.infer<typeof dailyMapRowSchema>;
export type Day = z.infer<typeof daySchema>;
export type Week = z.infer<typeof weekSchema>;
export type InterviewQuestion = z.infer<typeof interviewQuestionSchema>;
export type InterviewBank = z.infer<typeof interviewBankSchema>;
export type Orientation = z.infer<typeof orientationSchema>;
export type FixtureCollection = z.infer<typeof fixtureCollectionSchema>;
export type Manifest = z.infer<typeof manifestSchema>;
export type GeneratedBundle = z.infer<typeof generatedBundleSchema>;
